/**
 * Row selection for a paged, filtered list.
 *
 * Three list pages needed "tick some rows, then act on them", and none of them had
 * it. Written once rather than three times for the same reason `FilterBar` was: the
 * ordering, the indeterminate header state and the select-all-matching escalation are
 * *behaviour*, and behaviour copied into three files is behaviour that drifts.
 *
 * **Selection is scoped, and the scope clears it.** `scope` is a signature of the
 * current page plus filter state; whenever it changes, both the ticked rows and the
 * all-matching flag reset. That closes the worst hazard in the whole feature — tick
 * three rows, change a filter, hit the action, and act on rows that are no longer on
 * screen. It also keeps the id list bounded by one page, which is what lets the API
 * cap it at 250.
 *
 * **Two levels, like Gmail.** Ticking the header selects the current page and offers
 * "select all N matching these filters"; taking that offer sets `allMatching`, which
 * the page turns into a filter-mode request instead of an id-mode one. The distinction
 * matters because only one of those can act on rows the user never scrolled to.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'

export interface RowSelection<K extends string | number> {
  selected: ReadonlySet<K>
  count: number
  isSelected: (key: K) => boolean
  toggle: (key: K) => void
  /** `'some'` drives the header checkbox's indeterminate state. */
  pageState: 'none' | 'some' | 'all'
  /** Header checkbox: none/some → all of this page, all → none. */
  togglePage: () => void
  clear: () => void
  /**
   * The user asked to act on every row matching the filters, not just the ticked
   * ones. Pages send filter criteria rather than ids when this is set.
   */
  allMatching: boolean
  selectAllMatching: () => void
}

export function useRowSelection<K extends string | number>(
  pageKeys: K[],
  scope: string,
): RowSelection<K> {
  const [selected, setSelected] = useState<Set<K>>(() => new Set())
  const [allMatching, setAllMatching] = useState(false)

  // Acting on a selection made against a different result set is the failure this
  // hook exists to prevent, so the reset is not optional and not the caller's job.
  useEffect(() => {
    setSelected(new Set())
    setAllMatching(false)
  }, [scope])

  const toggle = useCallback((key: K) => {
    setAllMatching(false)
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const pageState = useMemo<'none' | 'some' | 'all'>(() => {
    if (pageKeys.length === 0) return 'none'
    const on = pageKeys.filter((k) => selected.has(k)).length
    if (on === 0) return 'none'
    return on === pageKeys.length ? 'all' : 'some'
  }, [pageKeys, selected])

  const togglePage = useCallback(() => {
    setAllMatching(false)
    setSelected((prev) => {
      const next = new Set(prev)
      // "some" resolves upward to "all", matching every table implementation the
      // user has met elsewhere; only a full page toggles back off.
      const everyKeyOn = pageKeys.length > 0 && pageKeys.every((k) => next.has(k))
      if (everyKeyOn) pageKeys.forEach((k) => next.delete(k))
      else pageKeys.forEach((k) => next.add(k))
      return next
    })
  }, [pageKeys])

  const clear = useCallback(() => {
    setSelected(new Set())
    setAllMatching(false)
  }, [])

  const selectAllMatching = useCallback(() => setAllMatching(true), [])

  return {
    selected,
    count: selected.size,
    isSelected: useCallback((key: K) => selected.has(key), [selected]),
    toggle,
    pageState,
    togglePage,
    clear,
    allMatching,
    selectAllMatching,
  }
}
